import { useEffect, useState } from "react";
import { Mail, MailX, MailCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Email-queue countdown chip + Cancel-send button.
 *
 * Rendered on completed admin-side request rows. Shows one of three states based on the
 * request's email tracking fields:
 *   • Queued — ticking countdown to email_send_at. Cancel button posts /cancel-email.
 *   • Sent — green checkmark with timestamp.
 *   • Cancelled — muted grey "Email cancelled".
 * Hidden entirely for client-side rows (admin-only feature).
 */
export default function EmailQueueChip({ request, onChange }) {
    const [cancelling, setCancelling] = useState(false);
    const [now, setNow] = useState(Date.now());

    // Tick once per second while a send is queued so the countdown stays live.
    useEffect(() => {
        if (!request.email_send_at || request.email_sent || request.email_cancelled) return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [request.email_send_at, request.email_sent, request.email_cancelled]);

    // Auto-refresh parent ~3s after the deadline so the chip flips to "Sent" without a manual reload.
    useEffect(() => {
        if (!request.email_send_at || request.email_sent || request.email_cancelled) return;
        const due = new Date(request.email_send_at).getTime();
        const ms = Math.max(0, due - Date.now()) + 3000;
        const id = setTimeout(() => onChange && onChange(), ms);
        return () => clearTimeout(id);
    }, [request.email_send_at, request.email_sent, request.email_cancelled, onChange]);

    const cancel = async (e) => {
        e.stopPropagation(); // don't toggle the row expander
        setCancelling(true);
        try {
            await api.post(`/requests/${request.id}/cancel-email`);
            onChange && onChange();
        } catch { /* silent */ }
        finally { setCancelling(false); }
    };

    // State 1: already sent
    if (request.email_sent) {
        return (
            <span
                className="inline-flex items-center gap-1 border border-emerald-500/50 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-300"
                data-testid={`email-sent-${request.id}`}
                title={request.email_sent_at ? `Sent ${new Date(request.email_sent_at).toLocaleString()}` : "Sent"}
            >
                <MailCheck className="w-3 h-3" /> Email sent
            </span>
        );
    }

    // State 2: explicitly cancelled
    if (request.email_cancelled && !request.email_send_at) {
        return (
            <span
                className="inline-flex items-center gap-1 border border-zinc-700 bg-zinc-900/50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500"
                data-testid={`email-cancelled-${request.id}`}
                title="Delivery email was cancelled"
            >
                <MailX className="w-3 h-3" /> Email cancelled
            </span>
        );
    }

    // State 3: queued — render only if there's a future send time
    if (!request.email_send_at) return null;
    const due = new Date(request.email_send_at).getTime();
    const remainMs = Math.max(0, due - now);
    const mm = Math.floor(remainMs / 60000);
    const ss = Math.floor((remainMs % 60000) / 1000);
    const label = remainMs <= 0 ? "Sending…" : `${mm}:${String(ss).padStart(2, "0")}`;

    return (
        <span
            className="inline-flex items-center gap-1 border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-300"
            data-testid={`email-queued-${request.id}`}
            title={`Email queued — will send at ${new Date(due).toLocaleTimeString()}`}
        >
            <Mail className="w-3 h-3" />
            Email · {label}
            <button
                type="button"
                onClick={cancel}
                disabled={cancelling}
                data-testid={`email-cancel-${request.id}`}
                className="ml-1.5 border-l border-amber-500/40 pl-1.5 text-amber-200 hover:text-amber-100 hover:underline disabled:opacity-60"
                aria-label="Cancel queued email"
            >
                {cancelling ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Cancel"}
            </button>
        </span>
    );
}
